import { ApiSchema } from "@api-types";
import { odataList } from "services/odataApi";
import { Globals } from "types/globals";

export namespace ResultsModuleHelpers {
  export const internalResultsLink = (
    projectId: number,
    path: string,
    urlParams?: Record<string, string | number | (string | number)[] | undefined | null>,
    inheritCurrentParams: boolean = true,
  ): string => {
    const mergedParams = new URLSearchParams();

    if (urlParams) {
      for (const [key, value] of Object.entries(urlParams)) {
        if (value === undefined || value === null) {
          continue;
        }

        if (Array.isArray(value)) {
          const sanitizedArray = value
            .filter((v): v is string | number => v !== undefined && v !== null)
            .map(String);

          if (sanitizedArray.length > 0) {
            mergedParams.set(key, `[${sanitizedArray.join(",")}]`);
          }
        } else {
          mergedParams.set(key, String(value));
        }
      }
    }

    if (inheritCurrentParams) {
      const currentSearchParams = new URLSearchParams(window.location.search);
      for (const [key, value] of currentSearchParams.entries()) {
        if (!mergedParams.has(key)) {
          mergedParams.set(key, value);
        }
      }
    }

    const queryString = mergedParams.toString();
    return `/results/${projectId}/${path}${queryString ? `?${queryString}` : ""}`;
  };

  export const getChartsRoute = (
    projectType: string | null | undefined,
    frameworkId: number | null | undefined,
    segmentQuestionId: number | null | undefined,
    firstFollowUpQuestion: { id: number; questionTypeId: number } | null | undefined,
  ): string => {
    switch (projectType) {
      case "idea":
      case "message":
        if (frameworkId === Globals.Frameworks.PriceSensitivity) return "price-sensitivity";
        if (frameworkId === Globals.Frameworks.MarketReach) return "market-reach";
        return "all";
      case "npd":
        return "npd";
      case "question":
        if (segmentQuestionId) return `segment-question/${segmentQuestionId}`;
        if (firstFollowUpQuestion) {
          const t = firstFollowUpQuestion.questionTypeId === Globals.QuestionType.text ? "text" : "qual";
          return `${t}/${firstFollowUpQuestion.id}`;
        }
        return "";
      default:
        return "";
    }
  };

  export const getKeys = (dictionary: { [key in number]: boolean }) =>
    Object.keys(dictionary).map((a) => dictionary[Number(a)]);

  export const getSubKeys = (dictionary: { [key in number]: { [key in number]: boolean } }) =>
    Object.keys(Object.values(dictionary)).map((a) => dictionary[Number(a)]);

  export const getTargetsReqBuilder = (projectId: number) =>
    odataList("getTargets")
      .select("id", "question", "questionId", "questionOptionId", "systemQuestionOption", "targetMode")
      .filter("projectId", "eq", projectId)
      .expandAndSelect("question", null, "questionTitle", "id")
      .expandAndSelect("questionOption", null, "optionText", "id")
      .expandAndSelect(
        "systemQuestionOption",
        (a) => a.expandAndSelect("systemQuestion", null, "questionLabel"),
        "answerLabel",
      );

  export const getProjectScheduleReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules").filter("projectId", "eq", projectId).orderBy("createdAt");

  export const getPanelTypesReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules")
      .select("id", "regionId", "isCustomerPanel")
      .filter("projectId", "eq", projectId)
      .filter("surveyRunId", "ne", null);

  type ProjectScheduleForRunDate = Pick<
    ApiSchema.ProjectSchedule,
    "scheduleDate" | "surveyRunId" | "regionId"
  >;

  export const getProjectRunDate = (params: {
    launchDate?: Date | string | null;
    createdAt?: Date | string | null;
    projectSchedules?: ProjectScheduleForRunDate[] | null;
    regionId?: number | null;
  }): Date | string | undefined => {
    const baseDate = params.launchDate ?? params.createdAt;

    if (!baseDate) {
      return undefined;
    }

    if (!params.projectSchedules || !params.regionId) {
      return baseDate;
    }

    const completedSchedules = params.projectSchedules.filter(
      (schedule) => schedule.surveyRunId !== null && schedule.regionId === params.regionId,
    );

    if (completedSchedules.length === 0) {
      return baseDate;
    }

    const latestScheduleDate = completedSchedules.reduce<string | undefined>((latest, schedule) => {
      if (!schedule.scheduleDate) {
        return latest;
      }

      if (!latest) {
        return schedule.scheduleDate;
      }

      return new Date(schedule.scheduleDate) > new Date(latest) ? schedule.scheduleDate : latest;
    }, undefined);

    return latestScheduleDate ?? baseDate;
  };

  export const isWaveMode = (
    project?: Pick<ApiSchema.Project, "scheduleStartDate" | "wavesRequired" | "scheduleFrequencyWeeks">,
  ) => {
    if (!project) {
      return false;
    }

    const scheduleStartDate = project.scheduleStartDate ?? null;
    const wavesRequired = !!project.wavesRequired;
    const scheduleFrequencyWeeks = project.scheduleFrequencyWeeks ?? null;

    if ((scheduleStartDate !== null && scheduleFrequencyWeeks !== null) || wavesRequired) {
      return true;
    }

    return false;
  };

  export const SystemQuestions: { [key in Globals.SystemQuestions]: string } = {
    [Globals.SystemQuestions.Generation]: "Generation",
    [Globals.SystemQuestions.HddStructure]: "Household structure",
    [Globals.SystemQuestions.Ethnicity]: "Ethnicity",
    [Globals.SystemQuestions.Region]: "Region",
    [Globals.SystemQuestions.TechAdop]: "Adoption behaviour",
    [Globals.SystemQuestions.Gender]: "Gender",
    [Globals.SystemQuestions.HddIncome]: "Household income",
    [Globals.SystemQuestions.Age]: "Age",
  };

  export const DebounceTimeMs = 1000;
}
